<!--suppress ALL -->
<template>
  <div class="g-table" :class="classNames">
    <div v-if="isLoading" class="loading-animation">
      <i class="fas fa-spinner fa-6x fa-spin"></i>
    </div>
    <div class="g-table-wrapper">
      <div v-if="$scopedSlots.header || $slots.header" class="header-g-table">
        <slot name="header"></slot>
      </div>
      <table class="table" :class="tableClassNames">
        <thead :class="headerClassName">
        <slot name="thead" v-if="$scopedSlots.thead || $slots.thead"
              :colSpan="colSpan"/>
        <tr v-else>
          <th v-if="Array.isArray(selectedRows)" style="width: 50px"
              class="tooltip has-text-centered" data-tooltip="Select all">
            <g-checkbox v-model="selectAll"></g-checkbox>
          </th>
          <th v-if="showNumbering">
            <div class="inner-th has-text-centered">
              <a>
                <span>No</span>
              </a>
            </div>
          </th>
          <template v-for="(column, index) in columns">
            <g-table-column-head :sorting="column.sorting"
                                 :th-index="index" :width="column.width"
                                 :position="column.position" :th-key="column.key">
              {{column.name}}
            </g-table-column-head>
          </template>
        </tr>
        </thead>
        <tbody>
        <template v-for="(data, index) in datasCloned.filter(item => item.visible)">
          <tr @click="onClickedIndex(safeGetDataCloned(index, true))"
              :class="pickTrClassName(index)">
            <td v-if="Array.isArray(selectedRows)" class="has-text-centered">
              <g-checkbox @change="(evt) => onSelectedIndex(evt, indexNumbering(index))"
                          :value="selectedRows.some(row => row === indexNumbering(index))">
              </g-checkbox>
            </td>
            <td v-if="showNumbering" class="has-text-centered is-number" data-label="No.">
              <span>{{indexNumbering(index + 1)}}</span>
            </td>
            <slot v-if="$scopedSlots.default || $slots.default" :data="omitProperties(data)"
                  :index="safeGetDataCloned(index, true)" :currentPage="currentPage"/>
            <template v-else v-for="(value, key, indexChild) in omitProperties(data)">
              <g-table-column :label="columns[indexChild].name"
                              :position="columns[indexChild] && columns[indexChild].position">
                <span>{{value}}</span>
              </g-table-column>
            </template>
          </tr>
          <tr v-if="$scopedSlots.detail || $slots.detail">
            <td v-if="forceRenderDetail" colspan="100" class="td-detail">
              <div :class="`td-detail-wrapper ${detailsOpened[index] ? '' : 'is-narrowed'}`">
                <a class="button is-small is-info" @click="expandDetail(index)">
                  <i :class="`icon-chevron ${detailsOpened[index] ? '' : 'is-up'}`"></i>
                </a>
                <div class="slot-wrp">
                  <slot name="detail" :index="safeGetDataCloned(index, true)"
                        :currentPage="currentPage" :data="omitProperties(data)"/>
                </div>
              </div>
            </td>
          </tr>
        </template>
        <template v-if="!datasCloned.length">
          <tr v-if="$slots.empty">
            <td colspan="100" class="data-empty">
              <slot name="empty"></slot>
            </td>
          </tr>
          <tr v-else>
            <td colspan="100" class="has-text-centered data-empty">
              No Data displayed
            </td>
          </tr>
        </template>
        </tbody>
      </table>
    </div>
    <div style="margin-top: 1.25rem" v-if="usePaging && datasCloned.length" class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <nav class="pagination">
          <ul class="pagination-list">
            <li class="paging-has-icon le" v-if="currentPage > 1">
              <a class="pagination-link"
                 @click="onChangePage(paginations.find(({value}) => value === currentPage - 1))">
                <span class="icon_chevron_v is-left"></span>
              </a>
            </li>
            <li v-for="(page, index) in paginations" :key="`pagination-${index}`">
              <a :class="page.btnClassName"
                 @click="onChangePage(page)">
                {{page.value}}
              </a>
            </li>
            <li class="paging-has-icon ri" v-if="currentPage < maxPage">
              <a class="pagination-link"
                 @click="onChangePage(paginations.find(({value}) => value === currentPage + 1))">
                <span class="icon_chevron_v"></span>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Component, Prop, Mixin, Watch} from "annotation";
  import {cloneArray, omit} from '../../../../util/Underscore';
  import PagingMixin from './PagingMixin';
  import PropsMixin from './PropsMixin';
  import GTableColumnHead from './GTableColumnHead.vue';
  import GCheckbox from '../checkbox/GCheckbox.vue';
  import GTableColumn from './GTableColumn.vue';

  @Component({
    components: {
      'g-checkbox': GCheckbox,
      'g-table-column': GTableColumn,
      'g-table-column-head': GTableColumnHead,
    }
  })
  export default class GTable extends Mixin(PropsMixin, PagingMixin) {

    @Prop({default: true})
    public responsiveTable: boolean;

    @Prop({default: []})
    public columns: Array<string>;

    @Prop({default: false, type: Boolean})
    public showNumbering: boolean;

    @Prop({default: null})
    public selectedIndex: any;

    @Prop({default: []})
    public datas: Array<any>;

    // make sure length eq to datas.length
    @Prop({default: []})
    public trClassNames: Array<any>;

    @Prop({default: null})
    public selecteds: any;

    public datasCloned: Array<any> = [];

    public currentSorting: any = null;

    public isSortAscending: boolean = true;

    public selectAll: boolean = false;

    public detailsOpened: Array<boolean> = [];

    public forceRenderDetail: boolean = true;

    public isTableComponent: boolean = true;

    public selectedRows: Array<any> = this.selecteds || null;

    @Watch('datas', {immediate: true})
    public onDataChange(datas) {
      const {currentPage, perPage,  frontEndPagination, usePaging} = (this as any);

      this.datasCloned = datas.map((item, index) => {
        item.index = index;

        if (currentPage > 1 && frontEndPagination && usePaging) {
          const {start, end} = this.pageRange;
          item.visible = index >= start && index < end;
        } else {
          item.visible = usePaging ? (index < perPage) : true;
        }

        return item;
      });

      this.detailsOpened = new Array<boolean>((this.datasCloned || []).length).fill(false);
    }

    @Watch('selecteds')
    public onSelectedsChange(val: any) {
      this.$set(this.$data, 'selectedRows', val);
      // this.$set(this.$data, 'selectedRows', val || []);
    }

    @Watch('selectAll')
    public onToggleSelectAll(param: boolean) {
      let selecteds: Array<number> = [];

      if (param) {
        selecteds = new Array(this.datas.length).fill(null).map((item: any, idx: number) => idx);
      } else {
        selecteds = [];
      }

      this.$emit('update:selecteds', selecteds);
    }

    @Watch('currentPage')
    public onPageHasChange() {
      this.applyPagination();
      this.reRenderDetail();
    }

    public applyPagination() {
      const {start, end} = this.pageRange;
      const {frontEndPagination, perPage, usePaging} = this;

      if (frontEndPagination && perPage && usePaging) {
        this.datasCloned.map((item, index) => {
          item.visible = index >= start && index < end;

          return item;
        });
      }
    }

    public onClickedIndex(index) {
      if (this.selectedIndex !== null) {
        this.$emit('update:selectedIndex', index);
      }
    }

    public reRenderDetail() {
      if (this.$scopedSlots.detail || this.$slots.detail) {
        this.forceRenderDetail = false;
        this.detailsOpened = this.detailsOpened.map((item) => {
          item = false;

          return item;
        });

        this.$nextTick(() => this.forceRenderDetail = true);
      }
    }

    public get pageRange() {
      const {perPage, currentPage} = this;
      const start = (currentPage - 1) * perPage;
      const end = start + perPage;

      return {start, end};
    }

    public get colSpan() {
      return this.columns.length + (this.showNumbering ? 1 : 0);
    }

    public get tableClassNames() {
      const {bordered, striped, narrow, isFullwidth, hoverable, responsiveTable} = this;

      return {
        'is-narrow': narrow, 'is-striped': striped,
        'is-bordered': bordered, 'is-fullwidth': isFullwidth,
        'is-hoverable': hoverable, 'has-responsive-table': responsiveTable
      }
    }

    public get classNames() {
      return {'inline-block': !this.isFullwidth};
    }

    public expandDetail(index) {
      this.detailsOpened[index] = !this.detailsOpened[index];
      this.forceRenderDetail = false;
      this.$nextTick(() => this.forceRenderDetail = true);
    }

    public pickTrClassName(index: number) {
      const params = {'selected': this.selectedIndex === this.safeGetDataCloned(index, true)};

      if (this.trClassNames && this.trClassNames[index]) {
        params[this.trClassNames[index]] = true;
      }

      return params;
    }

    public indexNumbering(index, isFindProps: boolean = false) {
      const pagingIndex = ((this.currentPage - 1) * this.perPage) + index;

      return isFindProps ? (this.frontEndPagination ? pagingIndex : index) :
        this.usePaging ? pagingIndex : index;
    }

    public safeGetDataCloned(index, isFindProps: boolean = false) {
      return (this.datasCloned && this.datasCloned[this.indexNumbering(index, true)] &&
        this.datasCloned[this.indexNumbering(index, true)].index) || 0;
    }

    public onSortBy(index: number, key: string) {
      const hasListener = this.$listeners && this.$listeners.sort;
      this.isSortAscending = this.currentSorting === index ? !this.isSortAscending : true;
      this.currentSorting = index;

      if (key && this.datasCloned[0] && this.datasCloned[0][key] && !hasListener) {
        const sortFn = (a, b) => {
          if (typeof a[key] === 'string') {
            return this.isSortAscending ? (a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0) :
              (a[key] > b[key] ? -1 : a[key] < b[key] ? 1 : 0);
          }

          return this.isSortAscending ? a[key] - b[key] : b[key] - a[key];
        };

        this.datasCloned = this.datasCloned.sort(sortFn);
        this.applyPagination();
        this.reRenderDetail();
      }

      this.$emit('sort', (this.isSortAscending ? 'asc' : 'desc'), key);
    }

    public omitProperties(data: any = {}) {
      return omit(data, ['index', 'visible']);
    }

    public onSelectedIndex(checked, index) {
      let selecteds = cloneArray(this.selecteds);
      if (checked) {
        selecteds.push(index);
      } else {
        selecteds = selecteds.filter(item => item !== index);
      }

      this.$emit('update:selecteds', selecteds);
    }
  }
</script>

<style lang="scss">
  @import "../../../../style/variables_function";

  .g-table {
    position: relative;

    thead {
      @each $name, $pair in $colors_type {
        &.is-#{$name} {
          $color: nth($pair, 1);
          $color-invert: nth($pair, 2);
          background-color: $color;

          th, th a:not(.button) span {
            color: $color-invert;
          }

          .icon-arrow {
            background-image: url(arrow($color-invert));

            &.is-up {
              background-image: url(arrow($color-invert, false));
            }
          }

          th.has-pointer:hover {
            background-color: lighten($color, 30%);
          }
        }
      }
    }

    .loading-animation {
      z-index: 1;
      text-align: center;
      position: absolute;
      height: 100%;
      width: 100%;
      background-color: rgba(255, 255, 255, .7);

      > i {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: $info;
      }
    }

    .icon-arrow {
      background-image: url(arrow($grey-darker));

      &.is-up {
        background-image: url(arrow($grey-darker, false));
      }
    }

    .icon_chevron_v {
      background-image: url(chevron_vertical($grey-darker));

      &.is-left {
        background-image: url(chevron_vertical($grey-darker, false));
      }
    }

    thead {
      th {
        .icon-arrow {
          margin-left: .5rem;
        }

        a {
          display: flex;
          align-items: center;

          span {
            color: $grey-darker;
          }

          cursor: auto;
        }

        &.has-pointer {
          &:hover {
            background-color: $white-ter;
          }

          a {
            cursor: pointer;
          }
        }

        .inner-th.has-text-centered {
          a {
            justify-content: center;
          }
        }

        .inner-th.has-text-left {
          a {
            justify-content: flex-start;
          }
        }

        .inner-th.has-text-right {
          a {
            justify-content: flex-end;
          }
        }
      }
    }

    .data-empty {
      min-height: 150px;
    }

    .header-g-table {
      position: relative;
    }

    .pagination-link:not(.is-current) {
      background-color: white;
    }

    td.td-detail {
      padding: 0;
      box-shadow: inset 0 3px 10px $white-ter;
      position: relative;

      .td-detail-wrapper {
        text-align: center;
        position: relative;
        padding: 0 1rem 1rem;

        > a {
          opacity: 0;
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }

        &.is-narrowed {
          padding: 0;
          position: absolute;
          margin: 0 auto;
          width: 100%;
          bottom: 0;

          .slot-wrp {
            display: none;
          }

          > a {
            border-top-left-radius: 2px;
            border-top-right-radius: 2px;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }
        }

        .icon-chevron {
          background-image: url(chevron(white));

          &.is-up {
            background-image: url(chevron(white, false));
          }
        }
      }
    }

    tr:hover + tr .td-detail a, tr .td-detail:hover a {
      opacity: 1.0;
    }

    @media screen and (max-width: 768px) {
      table.has-responsive-table {
        thead {
          display: none;
        }

        tbody {
          tr {
            display: block;
            @include bulma_box_style;

            &:not(:last-child) {
              margin-bottom: 2rem;
            }

            td {
              display: flex;
              justify-content: space-between;

              &:not(.data-empty)::before {
                content: attr(data-label);
                font-weight: bold;
              }

              &.data-empty {
                align-items: center;
                justify-content: center;
              }

              span:only-child {
                text-align: right;
              }
            }
          }
        }
      }
    }

    .pagination-list li {
      list-style: none;
    }

    tr.selected {
      background-color: lighten($info, 20%) !important;

      span, a {
        color: white;
      }
    }

    &.inline-block {
      display: inline-block;
    }
  }
</style>
